'use client'; // it is required because of the use of `useSubscriptions` hook
import React from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@mentimeter/ragnar-ui/box';
import {
  MentimeterSymbol,
  MentimeterWordmark,
} from '@mentimeter/ragnar-visuals';
import type { DisplayProperty } from '@mentimeter/ragnar-styled';
import { VotingBar } from '@mentimeter/voting-bar';
import { Clickable } from '../Wrapper/Clickable';
import { Wide } from '../Layout/Wide';
import { isAuthenticated, type ContentUserT } from '../../state/user';
import { ActionMenu } from './ActionMenu';
import { useMenuActions } from './hooks/useMenuActions';
import { HeaderButton } from './HeaderButton';
import { MobileMenu } from './MobileMenu';
import { LoginButton } from './LoginButton';

interface HeaderT {
  showVotingBar?: boolean;
  user: ContentUserT;
}

/**
 * @deprecated This Header component is legacy and must not be used in new code.
 * Use HeaderV3 instead for new implementations.
 *
 * @see HeaderV3 - The current recommended header component
 * @legacy This component will be removed in a future version
 */
export function Header({ showVotingBar = false, user }: HeaderT) {
  const actions = useMenuActions(user);
  const t = useTranslations('first_impression.legacy.common');

  const displayOnDesktop = ['none', 'none', 'flex'];
  const displayOnMobile = ['flex', 'flex', 'none'] as DisplayProperty[];

  return (
    <Box
      as="header"
      top="space0"
      zIndex={10}
      width="100%"
      alignItems="center"
      justifyContent="center"
    >
      <Box as="nav" width="100%">
        <Box
          px="space4"
          py={['space4', null, 'space8']}
          width="100%"
          alignItems="center"
          borderBottomWidth="1px"
          borderStyle="solid"
          borderColor="borderWeak"
          bg="bg"
          justifyContent="center"
          height={4}
        >
          <Wide
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            zIndex={10}
          >
            <Box flex="1">
              <Clickable
                href="/"
                data-testid="page-header-logo"
                aria-label={t('page_links.to_mentimeter_aria')}
                overflow="visible"
                display="flex"
                flexDirection="row"
              >
                <Box display="flex" mr="space2">
                  <MentimeterSymbol width={28.31} />
                </Box>
                <Box display={['none', 'flex']}>
                  <MentimeterWordmark width={165} />
                </Box>
              </Clickable>
            </Box>

            <Box
              display={displayOnDesktop}
              as="ul"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
            >
              <ActionMenu
                actions={actions}
                overviewLabel={t('page_links.see_more')}
              />
            </Box>
            <Box
              flex="1"
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-end"
            >
              {!isAuthenticated(user) && (
                <Box display={['none', 'flex']}>
                  <LoginButton mx="space4" />
                </Box>
              )}
              <HeaderButton user={user} />
            </Box>
            <MobileMenu
              display={displayOnMobile}
              actions={actions}
              displayOnMobile={displayOnMobile}
              user={user}
            />
          </Wide>
        </Box>
      </Box>

      {showVotingBar && <VotingBar context="Homepage" />}
    </Box>
  );
}
